import { useState, type FormEvent } from 'react';
import {
  AffixedTextField,
  Button,
  Card,
  CardHeader,
  ErrorState,
  PageHeader,
  Row,
  SelectField,
  TextField,
} from '@sparkery/ui';
import { CHANNELS, type Channel, type ProgrammeSettings } from '@sparkery/contracts';
import { ApiError } from '../../lib/api.ts';
import { useSettings, useUpdateSettings } from '../../lib/partner-admin.ts';
import './settings.css';

/**
 * The programme-wide settings.
 *
 * Two groups, deliberately separated, because they look alike and behave nothing
 * alike:
 *
 * - **Link defaults** change nothing that already exists. Every link's URL is stored
 *   as generated, so a new campaign name applies only to links generated afterwards.
 * - **Commission** feeds the domain. The maturity window reaches only what the next
 *   scrape accrues; the programme start decides which orders are referrals at all, and
 *   the server refuses to move it once a statement has been generated.
 *
 * The two are separate CARDS rather than separate paragraphs of explanation: the
 * grouping is what says they behave differently, and the only prose left on the screen
 * is the warning that appears when the programme start is actually touched.
 */
export function SettingsView() {
  const settings = useSettings();
  // The mutation lives HERE, above the keyed form, so its confirmation survives the
  // remount a successful save causes. Inside the form it would be discarded by the
  // very save it was reporting.
  const update = useUpdateSettings();
  const current = settings.data;

  return (
    <>
      <PageHeader
        title="Programme settings"
        subtitle="Link defaults and commission rules, for every partner"
      />

      {settings.isError ? (
        <ErrorState
          title="Could not load settings"
          {...(settings.error instanceof Error ? { detail: settings.error.message } : {})}
          onRetry={() => void settings.refetch()}
        />
      ) : null}

      {current ? (
        // KEYED on the saved values, so the form remounts and re-seeds itself whenever
        // the record moves — after a save, or when another admin's change arrives on a
        // refetch. React's own answer to "reset state when a prop changes", and it
        // keeps the inputs plainly initialised rather than synchronised in an effect.
        <SettingsForm key={signatureOf(current)} settings={current} update={update} />
      ) : null}
    </>
  );
}

/** A compact identity for the saved record: any change to it remounts the form. */
function signatureOf(settings: ProgrammeSettings): string {
  return [
    settings.campaign,
    settings.fallbackDestination,
    settings.defaultChannel,
    settings.commissionMaturityDays,
    settings.commissionStartsAt,
  ].join('|');
}

function SettingsForm({
  settings: current,
  update,
}: {
  settings: ProgrammeSettings;
  update: ReturnType<typeof useUpdateSettings>;
}) {
  const [campaign, setCampaign] = useState(current.campaign);
  const [fallback, setFallback] = useState(current.fallbackDestination);
  const [channel, setChannel] = useState<Channel>(current.defaultChannel);
  const [maturityDays, setMaturityDays] = useState(String(current.commissionMaturityDays));
  const [startsAt, setStartsAt] = useState(current.commissionStartsAt);

  const changes = {
    ...(campaign.trim() === current.campaign ? {} : { campaign: campaign.trim() }),
    ...(fallback.trim() === current.fallbackDestination
      ? {}
      : { fallbackDestination: fallback.trim() }),
    ...(channel === current.defaultChannel ? {} : { defaultChannel: channel }),
    ...(Number(maturityDays) === current.commissionMaturityDays
      ? {}
      : { commissionMaturityDays: Number(maturityDays) }),
    ...(startsAt === current.commissionStartsAt ? {} : { commissionStartsAt: startsAt }),
  };

  const dirty = Object.keys(changes).length > 0;
  const startMoved = 'commissionStartsAt' in changes;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (dirty) update.mutate(changes);
  }

  return (
    <form className="settingsForm" onSubmit={submit}>
      <Card>
        <CardHeader title="Link defaults" meta="Applies to links generated from now on" />

        <div className="settingsFields">
          <TextField
            id="settings-campaign"
            label="Campaign"
            value={campaign}
            onChange={(event) => setCampaign(event.target.value)}
            spellCheck={false}
          />

          <TextField
            id="settings-fallback"
            label="Fallback destination"
            value={fallback}
            onChange={(event) => setFallback(event.target.value)}
            spellCheck={false}
          />

          <SelectField
            id="settings-channel"
            label="Default channel"
            value={channel}
            onChange={(event) => setChannel(event.target.value as Channel)}
            options={CHANNELS.map((option) => ({ value: option, label: option }))}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Commission" meta="Feeds the monthly run" />

        <div className="settingsFields">
          <AffixedTextField
            id="settings-maturity"
            label="Maturity window"
            value={maturityDays}
            onChange={(event) => setMaturityDays(event.target.value)}
            inputMode="numeric"
            inputWidth="6rem"
            suffix="days from the order's processed date"
          />

          <TextField
            id="settings-start"
            label="Programme start"
            inputClassName="settingsDate"
            type="date"
            value={startsAt}
            onChange={(event) => setStartsAt(event.target.value)}
            error={
              startMoved
                ? 'Changes which orders count as referrals. Refused once a statement exists.'
                : undefined
            }
          />
        </div>
      </Card>

      <Card className="settingsActionsCard">
        <Row gap="16px">
          <Button type="submit" disabled={!dirty || update.isPending}>
            {update.isPending ? 'Saving…' : 'Save settings'}
          </Button>
          <span className="settingsNote">{dirty ? 'Unsaved changes.' : 'Nothing changed.'}</span>
        </Row>

        {update.error ? (
          <p className="settingsError" role="alert">
            {update.error instanceof ApiError
              ? update.error.message
              : 'Could not save those settings.'}
          </p>
        ) : null}

        {update.data ? (
          <p className="settingsOk" role="status">
            {update.data.changed.length > 0
              ? `Saved — ${update.data.changed.join(', ')} updated.`
              : 'Nothing had changed, so nothing was saved.'}
          </p>
        ) : null}
      </Card>
    </form>
  );
}
